<template>
  <div
    class="gerenciador-parcerias-area"
    role="region"
    aria-labelledby="gerenciador-titulo"
    tabindex="-1"
  >
    <!-- ================================================================= -->
    <!-- CABEÇALHO DA ÁREA DE GERENCIAMENTO                                -->
    <!-- ================================================================= -->
    <header class="gerenciador-header">
      <div class="gerenciador-header-info">
        <div class="titulo-wrapper">
          <button
            type="button"
            class="btn-voltar-topo"
            :aria-label="estadoAtual === 'LISTA' ? 'Voltar para o painel de parcerias' : 'Voltar para a lista de ações'"
            @click="estadoAtual === 'LISTA' ? fechar() : voltarParaLista()"
          >
            <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
            <span>{{ estadoAtual === 'LISTA' ? 'Voltar ao painel' : 'Voltar à lista' }}</span>
          </button>
          <div>
            <h2 id="gerenciador-titulo" class="gerenciador-titulo">
              {{
                estadoAtual === 'INSERIR'
                  ? 'Inserir Parceria na Ação'
                  : estadoAtual === 'EDITAR'
                  ? 'Editar Formalização da Parceria'
                  : 'Gerenciamento de Parcerias por Ação'
              }}
            </h2>
            <p class="gerenciador-subtitulo">
              {{
                estadoAtual === 'INSERIR'
                  ? 'Preencha os dados da instituição para vincular a nova parceria.'
                  : estadoAtual === 'EDITAR'
                  ? 'Atualize o status de formalização jurídica desta parceria.'
                  : 'Consulte ações de extensão e gerencie suas parcerias institucionais.'
              }}
            </p>
          </div>
        </div>
      </div>

      <button
        type="button"
        class="btn-fechar-secao"
        aria-label="Voltar para a análise de parcerias"
        @click="fechar"
      >
        <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        <span>Fechar</span>
      </button>
    </header>

    <!-- ================================================================= -->
    <!-- CORPO DA ÁREA DE GERENCIAMENTO                                    -->
    <!-- ================================================================= -->
    <div class="gerenciador-body">
          <!-- FEEDBACK (SUCESSO / ERRO) -->
          <div
            v-if="feedback.texto"
            class="alerta-feedback"
            :class="feedback.tipo"
            role="alert"
          >
            <div class="alerta-conteudo">
              <i
                :class="
                  feedback.tipo === 'sucesso'
                    ? 'fa-solid fa-circle-check'
                    : 'fa-solid fa-circle-exclamation'
                "
                aria-hidden="true"
              ></i>
              <span>{{ feedback.texto }}</span>
            </div>
            <button
              type="button"
              class="btn-fechar-alerta"
              aria-label="Fechar notificação"
              @click="limparFeedback"
            >
              ×
            </button>
          </div>

          <!-- =============================================================== -->
          <!-- ESTADO 1: LISTAGEM DE AÇÕES (LAYOUT ENXUTO EM 2 COLUNAS)         -->
          <!-- =============================================================== -->
          <div v-if="estadoAtual === 'LISTA'" class="secao-lista">
            <!-- BARRA DE BUSCA COM DEBOUNCE -->
            <div class="barra-pesquisa">
              <div class="campo-pesquisa-wrapper">
                <i class="fa-solid fa-magnifying-glass icone-busca" aria-hidden="true"></i>
                <input
                  id="busca-acoes-input"
                  ref="buscaInputRef"
                  type="text"
                  class="input-busca"
                  :value="termoBusca"
                  placeholder="Buscar ação em execução por nome, coordenador ou ano..."
                  aria-label="Buscar ações de extensão"
                  @input="onInputBusca"
                />

                <!-- FEEDBACK VISUAL DISCRETO (SPINNER OU BOTÃO LIMPAR) -->
                <div class="indicador-busca">
                  <span
                    v-if="digitandoBusca || carregandoAcoes"
                    class="spinner-busca-sutil"
                    title="Buscando ações..."
                    aria-label="Buscando ações..."
                  >
                    <i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
                  </span>
                  <button
                    v-else-if="termoBusca"
                    type="button"
                    class="btn-limpar-campo"
                    aria-label="Limpar campo de busca"
                    @click="limparBusca"
                  >
                    <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              <div class="meta-busca">
                <span v-if="!carregandoAcoes">
                  <strong>{{ totalAcoes }}</strong> {{ totalAcoes === 1 ? 'ação encontrada' : 'ações encontradas' }}
                  <span v-if="buscaAtiva"> para "<em>{{ buscaAtiva }}</em>"</span>
                </span>
                <span v-else>Atualizando lista...</span>
              </div>
            </div>

            <!-- ESTADO: CARREGANDO INICIAL -->
            <div v-if="carregandoAcoes && acoes.length === 0" class="estado-feedback-vazio">
              <div class="spinner-principal" aria-hidden="true"></div>
              <p>Carregando ações de extensão...</p>
            </div>

            <!-- ESTADO: ERRO -->
            <div v-else-if="erroAcoes" class="estado-feedback-vazio estado-erro">
              <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
              <p>{{ erroAcoes }}</p>
              <button
                type="button"
                class="btn-tentar-novamente"
                @click="carregarAcoes(paginaAtual)"
              >
                Tentar novamente
              </button>
            </div>

            <!-- ESTADO: VAZIO -->
            <div v-else-if="acoes.length === 0" class="estado-feedback-vazio">
              <i class="fa-regular fa-folder-open" aria-hidden="true"></i>
              <h3>Nenhuma ação encontrada</h3>
              <p>Não encontramos ações para os critérios informados.</p>
              <button
                v-if="termoBusca"
                type="button"
                class="btn-limpar-filtro-vazio"
                @click="limparBusca"
              >
                Limpar busca
              </button>
            </div>

            <!-- LISTA DE CARDS ENXUTOS (2 COLUNAS) -->
            <div
              v-else
              class="lista-cards-acoes"
              :class="{ 'lista-opaca': carregandoAcoes }"
            >
              <article
                v-for="acao in acoes"
                :key="acao.id"
                class="card-acao"
                :class="{ 'card-expandido': acaoExpandidaId === acao.id }"
              >
                <!-- LINHA PRINCIPAL: ESQUERDA (3 LINHAS) + DIREITA (AÇÕES) -->
                <div class="card-linha-principal">
                  <!-- COLUNA ESQUERDA: EXATAMENTE 3 LINHAS (NOME, COORDENADOR, ANO) -->
                  <div class="card-coluna-info">
                    <h3 class="card-nome-acao" :title="acao.titulo">
                      {{ acao.titulo }}
                    </h3>

                    <div class="card-meta-linha">
                      <span class="card-meta-rotulo">Coordenador(a):</span>
                      <span class="card-meta-valor">{{ acao.nomeCoordenador || 'Não informado' }}</span>
                    </div>

                    <div class="card-meta-linha">
                      <span class="card-meta-rotulo">Ano:</span>
                      <span class="card-meta-valor">{{ extrairAnoAcao(acao) }}</span>
                    </div>
                  </div>

                  <!-- COLUNA DIREITA: BOTÕES DE AÇÃO ALINHADOS VERTICALMENTE AO CENTRO -->
                  <div class="card-coluna-acoes">
                    <button
                      type="button"
                      class="btn-card-parcerias"
                      :class="{ ativo: acaoExpandidaId === acao.id }"
                      :aria-expanded="acaoExpandidaId === acao.id"
                      :aria-label="`Ver parcerias da ação ${acao.titulo}`"
                      @click="alternarExpansao(acao)"
                    >
                      <span>{{ obterTextoBotaoParcerias(acao.id) }}</span>
                      <i
                        class="fa-solid"
                        :class="acaoExpandidaId === acao.id ? 'fa-chevron-up' : 'fa-chevron-down'"
                        aria-hidden="true"
                      ></i>
                    </button>

                    <button
                      type="button"
                      class="btn-card-inserir"
                      :aria-label="`Inserir nova parceria na ação ${acao.titulo}`"
                      @click="iniciarInsercao(acao)"
                    >
                      <i class="fa-solid fa-plus" aria-hidden="true"></i>
                      <span>Inserir parceria</span>
                    </button>
                  </div>
                </div>

                <!-- SUB-PAINEL DE PARCERIAS VINCULADAS (EXPANSÃO ELEGANTE) -->
                <div v-if="acaoExpandidaId === acao.id" class="subpainel-parcerias">
                  <div class="subpainel-cabecalho">
                    <span class="subpainel-titulo">
                      <i class="fa-solid fa-handshake" aria-hidden="true"></i>
                      Parcerias vinculadas a esta ação
                    </span>
                    <button
                      type="button"
                      class="btn-subpainel-adicionar"
                      @click="iniciarInsercao(acao)"
                    >
                      <i class="fa-solid fa-plus" aria-hidden="true"></i>
                      <span>Nova parceria</span>
                    </button>
                  </div>

                  <!-- CARREGANDO SUB-LISTA -->
                  <div v-if="carregandoParcerias[acao.id]" class="subpainel-loading">
                    <i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
                    <span>Consultando parcerias cadastradas...</span>
                  </div>

                  <!-- NENHUMA PARCERIA -->
                  <div
                    v-else-if="!parceriasPorAcao[acao.id] || parceriasPorAcao[acao.id].length === 0"
                    class="subpainel-vazio"
                  >
                    <p>Nenhuma parceria cadastrada para esta ação de extensão.</p>
                  </div>

                  <!-- LISTAGEM ENXUTA DE PARCERIAS -->
                  <div v-else class="subpainel-lista">
                    <div
                      v-for="parceria in parceriasPorAcao[acao.id]"
                      :key="parceria.id"
                      class="item-parceria-enxuto"
                    >
                      <div class="item-parceria-info">
                        <strong class="item-parceria-nome">
                          {{ parceria.nomeInstituicao }}
                        </strong>
                        <div class="item-parceria-meta">
                          <span v-if="parceria.cnpj" class="item-parceria-cnpj">
                            CNPJ: {{ formatarCnpjExibicao(parceria.cnpj) }}
                          </span>
                          <span v-if="parceria.tipoInstituicao" class="item-parceria-tipo">
                            • {{ parceria.tipoInstituicao }}
                          </span>
                        </div>
                      </div>

                      <div class="item-parceria-status-acoes">
                        <span
                          class="badge-formalizacao"
                          :class="parceria.jaFormalizada ? 'status-formalizada' : 'status-nao-formalizada'"
                        >
                          <i
                            :class="
                              parceria.jaFormalizada
                                ? 'fa-solid fa-circle-check'
                                : 'fa-regular fa-circle-dot'
                            "
                            aria-hidden="true"
                          ></i>
                          {{ parceria.jaFormalizada ? 'Formalizada' : 'Não formalizada' }}
                        </span>

                        <button
                          type="button"
                          class="btn-editar-parceria"
                          :aria-label="`Editar status de formalização de ${parceria.nomeInstituicao}`"
                          @click="iniciarEdicao(acao, parceria)"
                        >
                          <i class="fa-solid fa-pen-to-square" aria-hidden="true"></i>
                          <span>Editar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>

          <!-- =============================================================== -->
          <!-- ESTADO 2: FORMULÁRIO DE INSERÇÃO DE PARCERIA NA AÇÃO             -->
          <!-- =============================================================== -->
          <div v-else-if="estadoAtual === 'INSERIR'" class="secao-formulario">
            <!-- BANNER COMPACTO DA AÇÃO SELECIONADA -->
            <div class="resumo-acao-alvo">
              <span class="alvo-etiqueta">Ação selecionada</span>
              <h3 class="alvo-titulo">{{ acaoSelecionada?.titulo }}</h3>
              <div class="alvo-meta">
                <span>Coordenador(a): <strong>{{ acaoSelecionada?.nomeCoordenador || 'Não informado' }}</strong></span>
                <span>• Ano: <strong>{{ extrairAnoAcao(acaoSelecionada) }}</strong></span>
              </div>
            </div>

            <!-- FORMULÁRIO -->
            <form class="form-container" @submit.prevent="salvarNovaParceria">
              <div class="form-grid">
                <!-- NOME DA INSTITUIÇÃO -->
                <div class="form-grupo col-span-2">
                  <label for="form-nome-instituicao" class="form-label">
                    Nome da Instituição Parceira <span class="campo-obrigatorio">*</span>
                  </label>
                  <input
                    id="form-nome-instituicao"
                    v-model="formInsercao.nomeInstituicao"
                    type="text"
                    class="form-input"
                    :class="{ 'form-input-erro': errosForm.nomeInstituicao }"
                    placeholder="Ex: Empresa Parceira Ltda / Secretaria Municipal de Saúde"
                    required
                    aria-required="true"
                  />
                  <span v-if="errosForm.nomeInstituicao" class="mensagem-erro-campo">
                    {{ errosForm.nomeInstituicao }}
                  </span>
                </div>

                <!-- CNPJ -->
                <div class="form-grupo">
                  <label for="form-cnpj" class="form-label">
                    CNPJ da Instituição <span class="rotulo-opcional">(opcional)</span>
                  </label>
                  <input
                    id="form-cnpj"
                    v-model="formInsercao.cnpj"
                    type="text"
                    class="form-input"
                    placeholder="00.000.000/0000-00 (opcional)"
                    maxlength="18"
                    @input="aplicarMascaraCnpjForm"
                  />
                </div>

                <!-- TIPO DE INSTITUIÇÃO -->
                <div class="form-grupo">
                  <label for="form-tipo-instituicao" class="form-label">
                    Tipo de Instituição
                  </label>
                  <select
                    id="form-tipo-instituicao"
                    v-model="formInsercao.tipoInstituicao"
                    class="form-select"
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="Empresa Privada">Empresa Privada</option>
                    <option value="Órgão Público Municipal">Órgão Público Municipal</option>
                    <option value="Órgão Público Estadual">Órgão Público Estadual</option>
                    <option value="Órgão Público Federal">Órgão Público Federal</option>
                    <option value="ONG / OSC">ONG / OSC (Sociedade Civil)</option>
                    <option value="Fundação">Fundação</option>
                    <option value="Instituição de Ensino Superior">Instituição de Ensino Superior</option>
                    <option value="Escola Pública">Escola Pública</option>
                    <option value="Escola Privada">Escola Privada</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>

                <!-- ESTRANGEIRO -->
                <div class="form-grupo">
                  <label class="form-label">Instituição Internacional / Estrangeira?</label>
                  <div class="form-radios-inline">
                    <label class="radio-label">
                      <input
                        v-model="formInsercao.estrangeiro"
                        type="radio"
                        :value="false"
                        name="estrangeiro-radio"
                      />
                      <span>Não (Nacional)</span>
                    </label>
                    <label class="radio-label">
                      <input
                        v-model="formInsercao.estrangeiro"
                        type="radio"
                        :value="true"
                        name="estrangeiro-radio"
                      />
                      <span>Sim (Internacional)</span>
                    </label>
                  </div>
                </div>

                <!-- PAÍS -->
                <div class="form-grupo">
                  <label for="form-pais" class="form-label">
                    País
                  </label>
                  <input
                    id="form-pais"
                    v-model="formInsercao.pais"
                    type="text"
                    class="form-input"
                    placeholder="Ex: Brasil"
                  />
                </div>

                <!-- FORMAS DE INSERÇÃO (SIGAA) -->
                <div class="form-grupo col-span-2 bloco-formas-insercao">
                  <label class="form-label">
                    Formas de Inserção <span class="rotulo-opcional">(opcional)</span>
                  </label>
                  <p class="form-dica">
                    Selecione as formas de inserção da instituição parceira (padrão SIGAA):
                  </p>

                  <div class="grid-formas-insercao">
                    <label
                      v-for="opcao in opcoesFormasInsercao"
                      :key="opcao"
                      class="checkbox-forma-item"
                      :class="{ ativo: formInsercao.formasInsercao.includes(opcao) }"
                    >
                      <input
                        type="checkbox"
                        :value="opcao"
                        v-model="formInsercao.formasInsercao"
                      />
                      <span>{{ opcao }}</span>
                    </label>
                  </div>
                </div>

                <!-- CNPJ FUNDAÇÃO DE APOIO -->
                <div class="form-grupo">
                  <label for="form-cnpj-fundacao" class="form-label">
                    CNPJ da Fundação de Apoio (se houver)
                  </label>
                  <input
                    id="form-cnpj-fundacao"
                    v-model="formInsercao.cnpjFundacao"
                    type="text"
                    class="form-input"
                    placeholder="00.000.000/0000-00"
                    maxlength="18"
                    @input="aplicarMascaraCnpjFundacao"
                  />
                </div>

                <!-- CAMPO FORMALIZADA (DESTAQUE) -->
                <div class="form-grupo col-span-2 bloco-campo-formalizacao">
                  <label class="form-label">
                    Status de Formalização da Parceria <span class="campo-obrigatorio">*</span>
                  </label>
                  <p class="form-dica">
                    Indica se já existe convênio ou instrumento jurídico formalizado com a UFC.
                  </p>

                  <div class="seletor-cards-formalizacao">
                    <label
                      class="card-seletor"
                      :class="{ selecionado: formInsercao.jaFormalizada === false }"
                    >
                      <input
                        v-model="formInsercao.jaFormalizada"
                        type="radio"
                        :value="false"
                        name="insercao-formalizada"
                      />
                      <div class="card-seletor-conteudo">
                        <div class="seletor-icone nao-formalizada">
                          <i class="fa-regular fa-clock" aria-hidden="true"></i>
                        </div>
                        <div>
                          <strong>Não formalizada</strong>
                          <small>Em fase de articulação, contato preliminar ou informal.</small>
                        </div>
                      </div>
                    </label>

                    <label
                      class="card-seletor"
                      :class="{ selecionado: formInsercao.jaFormalizada === true }"
                    >
                      <input
                        v-model="formInsercao.jaFormalizada"
                        type="radio"
                        :value="true"
                        name="insercao-formalizada"
                      />
                      <div class="card-seletor-conteudo">
                        <div class="seletor-icone formalizada">
                          <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
                        </div>
                        <div>
                          <strong>Formalizada</strong>
                          <small>Possui convênio, acordo ou termo vigente formalizado.</small>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <!-- BOTÕES DO FORMULÁRIO -->
              <div class="form-botoes-rodape">
                <button
                  type="button"
                  class="btn-secundario"
                  :disabled="salvando"
                  @click="voltarParaLista"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  class="btn-primario"
                  :disabled="salvando"
                >
                  <i v-if="salvando" class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
                  <i v-else class="fa-solid fa-check" aria-hidden="true"></i>
                  <span>{{ salvando ? 'Salvando...' : 'Salvar Parceria' }}</span>
                </button>
              </div>
            </form>
          </div>

          <!-- =============================================================== -->
          <!-- ESTADO 3: EDITAR APENAS O CAMPO FORMALIZADA (SIM / NÃO)          -->
          <!-- =============================================================== -->
          <div v-else-if="estadoAtual === 'EDITAR'" class="secao-formulario">
            <!-- RESUMO DA PARCERIA EM EDIÇÃO -->
            <div class="resumo-acao-alvo">
              <span class="alvo-etiqueta">Parceria selecionada</span>
              <h3 class="alvo-titulo">{{ parceriaSelecionada?.nomeInstituicao }}</h3>
              <div class="alvo-meta">
                <span>Ação vinculada: <strong>{{ acaoSelecionada?.titulo }}</strong></span>
                <span v-if="parceriaSelecionada?.cnpj">
                  • CNPJ: <strong>{{ formatarCnpjExibicao(parceriaSelecionada.cnpj) }}</strong>
                </span>
              </div>
            </div>

            <!-- FORMULÁRIO FOCADO EXCLUSIVAMENTE EM FORMALIZADA (SIM/NÃO) -->
            <form class="form-container" @submit.prevent="salvarEdicaoFormalizacao">
              <div class="form-grupo">
                <label class="form-label">
                  A parceria encontra-se formalizada? <span class="campo-obrigatorio">*</span>
                </label>
                <p class="form-dica">
                  Selecione a situação atual da parceria institucional:
                </p>

                <div class="seletor-cards-formalizacao">
                  <label
                    class="card-seletor"
                    :class="{ selecionado: formEdicao.jaFormalizada === false }"
                  >
                    <input
                      v-model="formEdicao.jaFormalizada"
                      type="radio"
                      :value="false"
                      name="edicao-formalizada"
                    />
                    <div class="card-seletor-conteudo">
                      <div class="seletor-icone nao-formalizada">
                        <i class="fa-regular fa-circle-dot" aria-hidden="true"></i>
                      </div>
                      <div>
                        <strong>Não formalizada</strong>
                        <small>A parceria ainda não possui convênio ou instrumento formal.</small>
                      </div>
                    </div>
                  </label>

                  <label
                    class="card-seletor"
                    :class="{ selecionado: formEdicao.jaFormalizada === true }"
                  >
                    <input
                      v-model="formEdicao.jaFormalizada"
                      type="radio"
                      :value="true"
                      name="edicao-formalizada"
                    />
                    <div class="card-seletor-conteudo">
                      <div class="seletor-icone formalizada">
                        <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
                      </div>
                      <div>
                        <strong>Sim, parceria formalizada</strong>
                        <small>Possui convênio, acordo ou termo assinado e vigente.</small>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <!-- BOTÕES DO FORMULÁRIO -->
              <div class="form-botoes-rodape">
                <button
                  type="button"
                  class="btn-secundario"
                  :disabled="salvando"
                  @click="voltarParaLista"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  class="btn-primario"
                  :disabled="salvando"
                >
                  <i v-if="salvando" class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
                  <i v-else class="fa-solid fa-check" aria-hidden="true"></i>
                  <span>{{ salvando ? 'Salvando...' : 'Salvar Alteração' }}</span>
                </button>
              </div>
            </form>
          </div>
        </div>

    <!-- ================================================================= -->
    <!-- RODAPÉ DA ÁREA (PAGINAÇÃO NO ESTADO LISTA)                        -->
    <!-- ================================================================= -->
    <footer v-if="estadoAtual === 'LISTA' && totalAcoes > 0" class="gerenciador-footer">
      <span class="info-paginacao">
        Página <strong>{{ paginaAtual + 1 }}</strong> de <strong>{{ totalPaginas }}</strong>
      </span>

      <div class="controles-paginacao">
        <button
          type="button"
          class="btn-pag-nav"
          :disabled="paginaAtual === 0 || carregandoAcoes"
          aria-label="Página anterior"
          @click="mudarPagina(paginaAtual - 1)"
        >
          <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
          <span>Anterior</span>
        </button>

        <button
          type="button"
          class="btn-pag-nav"
          :disabled="paginaAtual >= totalPaginas - 1 || carregandoAcoes"
          aria-label="Próxima página"
          @click="mudarPagina(paginaAtual + 1)"
        >
          <span>Próxima</span>
          <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, nextTick } from "vue";
import acaoService from "@/service/acaoService";
import parceriaService from "@/service/parceriaService";

const emit = defineEmits(["fechar"]);

// Referência para focar no campo de busca ao abrir
const buscaInputRef = ref(null);

// Estado atual do fluxo ('LISTA' | 'INSERIR' | 'EDITAR')
const estadoAtual = ref("LISTA");

// Controle de listagem e paginação
const carregandoAcoes = ref(false);
const erroAcoes = ref("");
const acoes = ref([]);
const totalAcoes = ref(0);
const totalPaginas = ref(1);
const paginaAtual = ref(0);
const tamanhoPagina = 8;

// Busca com debounce
const termoBusca = ref("");
const buscaAtiva = ref("");
const digitandoBusca = ref(false);
let debounceTimer = null;

// Expansão de cards e cache de parcerias por ação
const acaoExpandidaId = ref(null);
const parceriasPorAcao = reactive({});
const carregandoParcerias = reactive({});

// Elementos em foco para Inserir ou Editar
const acaoSelecionada = ref(null);
const parceriaSelecionada = ref(null);

// Opções de Formas de Inserção (conforme cadastro no SIGAA)
const opcoesFormasInsercao = [
  "Gera Demanda",
  "Cede Remunera",
  "Participa na Definição de Ações",
  "Participa do Financiamento com recursos financeiros",
  "Fornece instalações e/ou equipamentos",
  "Participa do Financiamento com recursos econômicos",
  "Patrocínio",
  "Outras Formas de Inserção",
];

// Formulário de inserção
const formInsercao = reactive({
  nomeInstituicao: "",
  cnpj: "",
  tipoInstituicao: "",
  estrangeiro: false,
  pais: "Brasil",
  jaFormalizada: false,
  cnpjFundacao: "",
  formasInsercao: [],
});
const errosForm = reactive({
  nomeInstituicao: "",
});

// Formulário de edição (apenas campo formalizada)
const formEdicao = reactive({
  jaFormalizada: false,
});

// Estado de submissão
const salvando = ref(false);

// Feedback
const feedback = reactive({
  texto: "",
  tipo: "sucesso",
});

function exibirFeedback(texto, tipo = "sucesso") {
  feedback.texto = texto;
  feedback.tipo = tipo;
  setTimeout(() => {
    if (feedback.texto === texto) {
      limparFeedback();
    }
  }, 5000);
}

function limparFeedback() {
  feedback.texto = "";
}

// =========================================================================
// MÉTODOS DE BUSCA COM DEBOUNCE (450ms)
// =========================================================================
function onInputBusca(event) {
  const valor = event.target.value;
  termoBusca.value = valor;

  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  // Se o usuário apagou tudo, reseta imediatamente sem esperar
  if (!valor || !valor.trim()) {
    digitandoBusca.value = false;
    buscaAtiva.value = "";
    acaoExpandidaId.value = null;
    carregarAcoes(0);
    return;
  }

  digitandoBusca.value = true;
  debounceTimer = setTimeout(() => {
    digitandoBusca.value = false;
    buscaAtiva.value = valor.trim();
    acaoExpandidaId.value = null;
    carregarAcoes(0);
  }, 450);
}

function limparBusca() {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  digitandoBusca.value = false;
  termoBusca.value = "";
  buscaAtiva.value = "";
  acaoExpandidaId.value = null;
  carregarAcoes(0);
  nextTick(() => {
    buscaInputRef.value?.focus();
  });
}

// =========================================================================
// CARREGAMENTO DE AÇÕES
// =========================================================================
async function carregarAcoes(pagina = 0) {
  carregandoAcoes.value = true;
  erroAcoes.value = "";

  try {
    const buscaParam = buscaAtiva.value?.trim() || null;
    const response = await acaoService.getAcoesFiltro(
      pagina,
      tamanhoPagina,
      null, // unidadeId
      "EM EXECUÇÃO", // Trazer apenas ações em execução conforme solicitado
      buscaParam,
      null // ano
    );

    const dados = response?.content
      ? response.content
      : Array.isArray(response)
      ? response
      : [];

    acoes.value = dados;
    totalAcoes.value = response?.totalElements ?? dados.length;
    totalPaginas.value = response?.totalPages ?? (dados.length > 0 ? 1 : 0);
    paginaAtual.value = pagina;
  } catch (error) {
    console.error("Erro ao carregar ações:", error);
    erroAcoes.value = "Não foi possível carregar as ações. Tente novamente.";
    acoes.value = [];
  } finally {
    carregandoAcoes.value = false;
    digitandoBusca.value = false;
  }
}

function mudarPagina(novaPagina) {
  if (novaPagina >= 0 && novaPagina < totalPaginas.value) {
    acaoExpandidaId.value = null;
    carregarAcoes(novaPagina);
  }
}

// =========================================================================
// PARCERIAS DE UMA AÇÃO
// =========================================================================
async function carregarParceriasDaAcao(acaoId) {
  if (!acaoId) return;
  carregandoParcerias[acaoId] = true;

  try {
    const response = await parceriaService.listarParceriasExternasPorAcao(acaoId);
    const lista = Array.isArray(response) ? response : response?.data || [];
    parceriasPorAcao[acaoId] = lista;
  } catch (error) {
    console.error(`Erro ao buscar parcerias da ação ${acaoId}:`, error);
    parceriasPorAcao[acaoId] = [];
  } finally {
    carregandoParcerias[acaoId] = false;
  }
}

async function alternarExpansao(acao) {
  if (!acao?.id) return;
  if (acaoExpandidaId.value === acao.id) {
    acaoExpandidaId.value = null;
  } else {
    acaoExpandidaId.value = acao.id;
    if (!parceriasPorAcao[acao.id]) {
      await carregarParceriasDaAcao(acao.id);
    }
  }
}

function obterTextoBotaoParcerias(acaoId) {
  if (carregandoParcerias[acaoId]) return "Carregando...";
  const parcerias = parceriasPorAcao[acaoId];
  if (Array.isArray(parcerias)) {
    return `Parcerias (${parcerias.length})`;
  }
  return "Ver parcerias";
}

// =========================================================================
// FLUXO DE INSERÇÃO
// =========================================================================
function iniciarInsercao(acao) {
  acaoSelecionada.value = acao;
  errosForm.nomeInstituicao = "";
  formInsercao.nomeInstituicao = "";
  formInsercao.cnpj = "";
  formInsercao.tipoInstituicao = "";
  formInsercao.estrangeiro = false;
  formInsercao.pais = "Brasil";
  formInsercao.jaFormalizada = false;
  formInsercao.cnpjFundacao = "";
  formInsercao.formasInsercao = [];
  estadoAtual.value = "INSERIR";
}

async function salvarNovaParceria() {
  errosForm.nomeInstituicao = "";
  if (!formInsercao.nomeInstituicao || !formInsercao.nomeInstituicao.trim()) {
    errosForm.nomeInstituicao = "O nome da instituição parceira é obrigatório.";
    return;
  }

  if (!acaoSelecionada.value?.id) {
    exibirFeedback("Erro: nenhuma ação de extensão selecionada.", "erro");
    return;
  }

  salvando.value = true;
  try {
    const formaInsercaoFinal =
      formInsercao.formasInsercao && formInsercao.formasInsercao.length > 0
        ? formInsercao.formasInsercao.join(", ")
        : null;

    const payload = {
      acaoExtensaoId: acaoSelecionada.value.id,
      codigoAcao: acaoSelecionada.value.codigoAcao || acaoSelecionada.value.sigla || "",
      nomeInstituicao: formInsercao.nomeInstituicao.trim(),
      cnpj: formInsercao.cnpj ? formInsercao.cnpj.replace(/\D/g, "") : null,
      tipoInstituicao: formInsercao.tipoInstituicao || null,
      estrangeiro: Boolean(formInsercao.estrangeiro),
      pais: formInsercao.estrangeiro ? (formInsercao.pais || "Estrangeiro") : (formInsercao.pais || "Brasil"),
      jaFormalizada: Boolean(formInsercao.jaFormalizada),
      cnpjFundacao: formInsercao.cnpjFundacao ? formInsercao.cnpjFundacao.replace(/\D/g, "") : null,
      formaInsercao: formaInsercaoFinal,
    };

    await parceriaService.criarParceriaExterna(payload);

    exibirFeedback(`Parceria com "${formInsercao.nomeInstituicao}" cadastrada com sucesso!`);
    await carregarParceriasDaAcao(acaoSelecionada.value.id);
    acaoExpandidaId.value = acaoSelecionada.value.id;
    estadoAtual.value = "LISTA";
  } catch (error) {
    console.error("Erro ao cadastrar parceria:", error);
    const msg =
      error?.response?.data?.message || "Ocorreu um erro ao salvar a parceria. Tente novamente.";
    exibirFeedback(msg, "erro");
  } finally {
    salvando.value = false;
  }
}

// =========================================================================
// FLUXO DE EDIÇÃO (EXCLUSIVO: CAMPO FORMALIZADA SIM/NÃO)
// =========================================================================
function iniciarEdicao(acao, parceria) {
  acaoSelecionada.value = acao;
  parceriaSelecionada.value = parceria;
  formEdicao.jaFormalizada = Boolean(parceria.jaFormalizada);
  estadoAtual.value = "EDITAR";
}

async function salvarEdicaoFormalizacao() {
  if (!parceriaSelecionada.value?.id) {
    exibirFeedback("Erro: parceria não identificada para edição.", "erro");
    return;
  }

  salvando.value = true;
  try {
    await parceriaService.atualizarFormalizacaoParceriaExterna(
      parceriaSelecionada.value.id,
      formEdicao.jaFormalizada
    );

    parceriaSelecionada.value.jaFormalizada = formEdicao.jaFormalizada;
    if (acaoSelecionada.value?.id && parceriasPorAcao[acaoSelecionada.value.id]) {
      const idx = parceriasPorAcao[acaoSelecionada.value.id].findIndex(
        (p) => p.id === parceriaSelecionada.value.id
      );
      if (idx !== -1) {
        parceriasPorAcao[acaoSelecionada.value.id][idx].jaFormalizada = formEdicao.jaFormalizada;
      }
    }

    exibirFeedback(
      `Status da parceria atualizado para: ${
        formEdicao.jaFormalizada ? 'Formalizada' : 'Não formalizada'
      }.`
    );
    estadoAtual.value = "LISTA";
  } catch (error) {
    console.error("Erro ao atualizar formalização:", error);
    const msg =
      error?.response?.data?.message || "Ocorreu um erro ao atualizar a formalização.";
    exibirFeedback(msg, "erro");
  } finally {
    salvando.value = false;
  }
}

// =========================================================================
// NAVEGAÇÃO E UTILITÁRIOS
// =========================================================================
function voltarParaLista() {
  estadoAtual.value = "LISTA";
  errosForm.nomeInstituicao = "";
}

function fechar() {
  emit("fechar");
}

function onKeydownGlobal(e) {
  if (e.key === "Escape") {
    fechar();
  }
}

function extrairAnoAcao(acao) {
  if (!acao) return "—";
  if (acao.ano) return acao.ano;
  if (acao.dataInicio) {
    const d = new Date(acao.dataInicio);
    if (!isNaN(d.getFullYear())) return d.getFullYear();
  }
  if (acao.dataFim) {
    const d = new Date(acao.dataFim);
    if (!isNaN(d.getFullYear())) return d.getFullYear();
  }
  return "Não informado";
}

function formatarCnpjExibicao(cnpj) {
  if (!cnpj) return "—";
  const num = String(cnpj).replace(/\D/g, "");
  if (num.length === 14) {
    return num.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  }
  return cnpj;
}

function aplicarMascaraCnpjForm(e) {
  let v = e.target.value.replace(/\D/g, "").slice(0, 14);
  if (v.length > 12) {
    v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, "$1.$2.$3/$4-$5");
  } else if (v.length > 8) {
    v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4})/, "$1.$2.$3/$4");
  } else if (v.length > 5) {
    v = v.replace(/^(\d{2})(\d{3})(\d{0,3})/, "$1.$2.$3");
  } else if (v.length > 2) {
    v = v.replace(/^(\d{2})(\d{0,3})/, "$1.$2");
  }
  formInsercao.cnpj = v;
}

function aplicarMascaraCnpjFundacao(e) {
  let v = e.target.value.replace(/\D/g, "").slice(0, 14);
  if (v.length > 12) {
    v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, "$1.$2.$3/$4-$5");
  } else if (v.length > 8) {
    v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4})/, "$1.$2.$3/$4");
  } else if (v.length > 5) {
    v = v.replace(/^(\d{2})(\d{3})(\d{0,3})/, "$1.$2.$3");
  } else if (v.length > 2) {
    v = v.replace(/^(\d{2})(\d{0,3})/, "$1.$2");
  }
  formInsercao.cnpjFundacao = v;
}

onMounted(() => {
  carregarAcoes(0);
  window.addEventListener("keydown", onKeydownGlobal);
  nextTick(() => {
    buscaInputRef.value?.focus();
  });
});

onUnmounted(() => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  window.removeEventListener("keydown", onKeydownGlobal);
});
</script>

<style scoped>
/* ========================================================================= */
/* ESTRUTURA DA ÁREA DE GERENCIAMENTO (EMBUTIDA NA SEÇÃO DE PARCERIAS)       */
/* ========================================================================= */
.gerenciador-parcerias-area {
  width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  outline: none;
  animation: fadeIn 0.18s ease-out;
}

/* CABEÇALHO */
.gerenciador-header {
  padding: 18px 24px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #ffffff;
}

.gerenciador-header-info {
  flex: 1;
  min-width: 0;
}

.titulo-wrapper {
  display: flex;
  align-items: center;
  gap: 14px;
}

.btn-voltar-topo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.btn-voltar-topo:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0f172a;
}

.gerenciador-titulo {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.015em;
  line-height: 1.25;
}

.gerenciador-subtitulo {
  margin: 3px 0 0;
  font-size: 0.83rem;
  color: #64748b;
  line-height: 1.35;
}

.btn-fechar-secao {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.btn-fechar-secao:hover {
  background: #f1f5f9;
  color: #0f172a;
}

/* CORPO */
.gerenciador-body {
  padding: 24px;
  background: #ffffff;
}

/* FEEDBACK TOAST / ALERTA */
.alerta-feedback {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 0.84rem;
  margin-bottom: 18px;
  animation: fadeIn 0.2s ease;
}

.alerta-conteudo {
  display: flex;
  align-items: center;
  gap: 8px;
}

.alerta-feedback.sucesso {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.alerta-feedback.erro {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.btn-fechar-alerta {
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 1.15rem;
  cursor: pointer;
  line-height: 1;
}

/* ========================================================================= */
/* BARRA DE PESQUISA COM DEBOUNCE                                           */
/* ========================================================================= */
.barra-pesquisa {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
}

.campo-pesquisa-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.icone-busca {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 0.88rem;
  pointer-events: none;
}

.input-busca {
  width: 100%;
  height: 42px;
  padding: 9px 38px 9px 38px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.88rem;
  color: #0f172a;
  background: #f8fafc;
  transition: all 0.15s ease;
}

.input-busca:focus {
  outline: none;
  background: #ffffff;
  border-color: #0f172a;
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.08);
}

.input-busca::placeholder {
  color: #94a3b8;
}

.indicador-busca {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
}

.spinner-busca-sutil {
  font-size: 0.88rem;
  color: #64748b;
}

.btn-limpar-campo {
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-limpar-campo:hover {
  background: #cbd5e1;
  color: #1e293b;
}

.meta-busca {
  font-size: 0.78rem;
  color: #64748b;
  padding-left: 2px;
}

/* ========================================================================= */
/* LISTA DE CARDS ENXUTOS (LAYOUT 2 COLUNAS)                                */
/* ========================================================================= */
.lista-cards-acoes {
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: opacity 0.2s ease;
}

.lista-cards-acoes.lista-opaca {
  opacity: 0.6;
  pointer-events: none;
}

.card-acao {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  transition: all 0.15s ease;
  overflow: hidden;
}

.card-acao:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.card-acao.card-expandido {
  border-color: #cbd5e1;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}

/* LINHA PRINCIPAL: ESQUERDA (3 LINHAS) + DIREITA (BOTÕES) */
.card-linha-principal {
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

/* COLUNA ESQUERDA: EXATAMENTE 3 LINHAS */
.card-coluna-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

/* LINHA 1: NOME DA AÇÃO (DESTAQUE TIPOGRÁFICO PRINCIPAL) */
.card-nome-acao {
  margin: 0;
  font-size: 0.96rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
  white-space: normal;
  overflow-wrap: break-word;
}

/* LINHAS 2 E 3: COORDENADOR E ANO */
.card-meta-linha {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.35;
}

.card-meta-rotulo {
  color: #64748b;
  font-weight: 500;
}

.card-meta-valor {
  color: #334155;
  font-weight: 500;
}

/* COLUNA DIREITA: BOTÕES ALINHADOS VERTICALMENTE AO CENTRO */
.card-coluna-acoes {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.btn-card-parcerias {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 13px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.btn-card-parcerias:hover,
.btn-card-parcerias.ativo {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0f172a;
}

.btn-card-inserir {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid #0f172a;
  border-radius: 8px;
  background: #0f172a;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.btn-card-inserir:hover {
  background: #1e293b;
  border-color: #1e293b;
}

/* ========================================================================= */
/* SUB-PAINEL DE PARCERIAS (EXPANSÃO ELEGANTE)                              */
/* ========================================================================= */
.subpainel-parcerias {
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
  padding: 14px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.subpainel-cabecalho {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.subpainel-titulo {
  font-size: 0.82rem;
  font-weight: 700;
  color: #475569;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.btn-subpainel-adicionar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #6d28d9;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-subpainel-adicionar:hover {
  background: #f3e8ff;
  border-color: #7c3aed;
}

.subpainel-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  font-size: 0.82rem;
  color: #64748b;
  justify-content: center;
}

.subpainel-vazio {
  padding: 10px 0;
  font-size: 0.82rem;
  color: #64748b;
}

.subpainel-vazio p {
  margin: 0;
}

.subpainel-lista {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-parceria-enxuto {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.item-parceria-info {
  flex: 1;
  min-width: 0;
}

.item-parceria-nome {
  display: block;
  font-size: 0.86rem;
  color: #0f172a;
  font-weight: 600;
}

.item-parceria-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 0.76rem;
  color: #64748b;
}

.item-parceria-status-acoes {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.badge-formalizacao {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
}

.badge-formalizacao.status-formalizada {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.badge-formalizacao.status-nao-formalizada {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}

.btn-editar-parceria {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
  color: #334155;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-editar-parceria:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0f172a;
}

/* ========================================================================= */
/* ESTADOS 2 E 3: FORMULÁRIOS (INSERIR / EDITAR)                             */
/* ========================================================================= */
.secao-formulario {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.resumo-acao-alvo {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 18px;
}

.alvo-etiqueta {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6d28d9;
  margin-bottom: 3px;
}

.alvo-titulo {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
}

.alvo-meta {
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #64748b;
  flex-wrap: wrap;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.col-span-2 {
  grid-column: 1 / -1;
}

.form-grupo {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
}

.campo-obrigatorio {
  color: #ef4444;
}

.form-dica {
  margin: -2px 0 6px;
  font-size: 0.78rem;
  color: #64748b;
}

.form-input,
.form-select {
  height: 40px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.86rem;
  color: #0f172a;
  background: #ffffff;
  transition: all 0.15s ease;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #0f172a;
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.08);
}

.form-input-erro {
  border-color: #ef4444;
}

.mensagem-erro-campo {
  font-size: 0.74rem;
  color: #ef4444;
  font-weight: 500;
}

.form-radios-inline {
  display: flex;
  align-items: center;
  gap: 18px;
  height: 40px;
}

.radio-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.84rem;
  color: #334155;
  cursor: pointer;
}

.rotulo-opcional {
  font-size: 0.74rem;
  font-weight: 500;
  color: #94a3b8;
  margin-left: 4px;
}

.bloco-formas-insercao {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
}

.grid-formas-insercao {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 14px;
  margin-top: 6px;
}

.checkbox-forma-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.82rem;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}

.checkbox-forma-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.checkbox-forma-item.ativo {
  border-color: #0f172a;
  background: #ffffff;
  color: #0f172a;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.checkbox-forma-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #0f172a;
  cursor: pointer;
}

/* SELETOR EM CARDS PARA FORMALIZADA */
.bloco-campo-formalizacao {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
}

.seletor-cards-formalizacao {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.card-seletor {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 12px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;
}

.card-seletor input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.card-seletor-conteudo {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.seletor-icone {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.seletor-icone.nao-formalizada {
  background: #fffbeb;
  color: #d97706;
}

.seletor-icone.formalizada {
  background: #ecfdf5;
  color: #059669;
}

.card-seletor-conteudo strong {
  display: block;
  font-size: 0.88rem;
  color: #0f172a;
}

.card-seletor-conteudo small {
  display: block;
  margin-top: 2px;
  font-size: 0.74rem;
  color: #64748b;
  line-height: 1.35;
}

.card-seletor:hover {
  border-color: #cbd5e1;
}

.card-seletor.selecionado {
  border-color: #0f172a;
  background: #f8fafc;
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.08);
}

/* BOTÕES DE FORMULÁRIO */
.form-botoes-rodape {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
}

.btn-secundario {
  height: 40px;
  padding: 0 18px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #475569;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-secundario:hover:not(:disabled) {
  background: #f8fafc;
  color: #0f172a;
}

.btn-primario {
  height: 40px;
  padding: 0 20px;
  border: 1px solid #0f172a;
  border-radius: 8px;
  background: #0f172a;
  color: #ffffff;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s ease;
}

.btn-primario:hover:not(:disabled) {
  background: #1e293b;
  border-color: #1e293b;
}

.btn-primario:disabled,
.btn-secundario:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ========================================================================= */
/* RODAPÉ DA ÁREA (PAGINAÇÃO)                                                */
/* ========================================================================= */
.gerenciador-footer {
  padding: 14px 24px;
  border-top: 1px solid #f1f5f9;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.info-paginacao {
  font-size: 0.8rem;
  color: #64748b;
}

.controles-paginacao {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-pag-nav {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-pag-nav:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-pag-nav:not(:disabled):hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #0f172a;
}

/* ========================================================================= */
/* ESTADOS VAZIOS E FEEDBACKS                                               */
/* ========================================================================= */
.estado-feedback-vazio {
  text-align: center;
  padding: 36px 20px;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.estado-feedback-vazio i {
  font-size: 2rem;
  color: #94a3b8;
}

.estado-feedback-vazio h3 {
  margin: 4px 0 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: #1e293b;
}

.estado-feedback-vazio p {
  margin: 0;
  font-size: 0.84rem;
}

.btn-limpar-filtro-vazio,
.btn-tentar-novamente {
  margin-top: 6px;
  padding: 6px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-limpar-filtro-vazio:hover,
.btn-tentar-novamente:hover {
  background: #f8fafc;
  color: #0f172a;
}

.spinner-principal {
  width: 32px;
  height: 32px;
  border: 3px solid #f1f5f9;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ========================================================================= */
/* RESPONSIVIDADE                                                           */
/* ========================================================================= */
@media (max-width: 768px) {
  .modal-backdrop-overlay {
    padding: 16px;
  }

  .modal-card {
    max-height: calc(100vh - 32px);
  }

  .card-linha-principal {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .card-coluna-acoes {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .seletor-cards-formalizacao {
    grid-template-columns: 1fr;
  }

  .item-parceria-enxuto {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .item-parceria-status-acoes {
    justify-content: space-between;
  }

  .gerenciador-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .gerenciador-footer {
    flex-direction: column;
    align-items: center;
  }
}

/* ========================================================================= */
/* ACESSIBILIDADE E MOVIMENTO REDUZIDO                                       */
/* ========================================================================= */
button:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 2px solid #0f172a;
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .gerenciador-parcerias-area,
  .alerta-feedback,
  .spinner-principal,
  .spinner-busca-sutil i {
    animation: none !important;
    transition: none !important;
  }
}
</style>
